/**
 * PanelMaterial — a swatch trigger over a grid of the pallet's materials, by name and by texture.
 *
 * THE TEXTURE, NOT A FLAT COLOUR. It was the colour for one reason: `MATERIAL_COLORS` is a lookup
 * and needs no texture load, and with three material fields in the whole dialog that was the right
 * trade. There are eleven now — the landform's five skin bands, the water, three region wall
 * colours, and the ground layers — and picking "the ground" out of a grid of averaged greens is not
 * a thing anyone can do. `useMaterialThumbnails` already renders every material at 48 px and caches
 * them for the page; the flat colour stays as the fallback while that resolves, and if the fetch
 * fails at all the control still works.
 *
 * NAMES TOO, for the same reason: a swatch is not an identifier, and `moss2` vs `moss` is exactly
 * the kind of pair the grid has to be able to distinguish.
 *
 * Opens UPWARD like the kit's other popovers: the panels sit low on the screen.
 */

import { useEffect, useRef, useState } from 'react';
import { MATERIAL_COLORS, MATERIAL_NAMES, Materials, type MaterialName } from '@worldify/shared';
import { useMaterialThumbnails } from '../useMaterialThumbnails';
import { CONTROL_H, MOTION, POPOVER_SURFACE, TRACK } from './tokens';

interface PanelMaterialProps {
  value: MaterialName | string;
  onChange: (value: MaterialName) => void;
  ariaLabel?: string;
}

const idOf = (name: string): number =>
  MATERIAL_NAMES.indexOf(name as MaterialName) >= 0 ? Materials.get(name as MaterialName) : -1;

const colorOf = (name: string): string => {
  const id = idOf(name);
  return (id >= 0 ? MATERIAL_COLORS[id] : undefined) ?? '#cccccc';
};

/** The swatch itself: the texture once it has loaded, its average colour until then. */
function Swatch({ name, thumbs, className }: {
  name: string; thumbs: string[] | null; className: string;
}) {
  const url = thumbs?.[idOf(name)];
  return (
    <span
      className={`${className} bg-cover bg-center`}
      style={{ background: url ? undefined : colorOf(name), backgroundImage: url ? `url(${url})` : undefined }}
    />
  );
}

export function PanelMaterial({ value, onChange, ariaLabel }: PanelMaterialProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const thumbs = useMaterialThumbnails();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`w-full ${CONTROL_H} ${TRACK} px-3 flex items-center gap-2 text-[13px] md:text-xs
          font-bold text-neutral-800 cursor-pointer hover:bg-neutral-200/70 ${MOTION}`}
      >
        <Swatch name={value} thumbs={thumbs} className="w-5 h-5 rounded-md shrink-0 ring-1 ring-inset ring-black/10" />
        <span className="truncate">{value}</span>
      </button>

      {open && (
        <div className={`absolute bottom-full left-0 right-0 mb-2 z-10 ${POPOVER_SURFACE} max-h-[45vh] overflow-y-auto`}>
          <div className="grid grid-cols-4 gap-1.5">
            {MATERIAL_NAMES.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); setOpen(false); }}
                className={`flex flex-col items-center gap-0.5 p-1 rounded-lg cursor-pointer ${MOTION} ${
                  name === value ? 'bg-[#93CB55]/25' : 'hover:bg-neutral-100'
                }`}
              >
                <Swatch
                  name={name}
                  thumbs={thumbs}
                  className={`w-full aspect-square rounded-md ring-1 ring-inset ${
                    name === value ? 'ring-2 ring-[#025C3B]' : 'ring-black/10'
                  }`}
                />
                <span className="w-full truncate text-center text-[9px] font-semibold text-neutral-500">{name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
